import React, { useEffect, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  getWalletBalanceURL,
  getWalletHistoryURL,
  requestWithdrawalURL
} from "../../../utils/urls";
import Layout from "../component/layout";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LOGO_BASE64 } from "../../../utils/logo64";

function Wallet() {
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawNote, setWithdrawNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const formatCurrency = (amount) => {
    if (typeof amount !== "number") return "KES 0.00";
    return `KES ${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const fetchWallet = useCallback(async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      const landlordId = await getItem("selectedCustomerId");

      console.log("🔍 Fetching wallet for:", { facilityId, landlordId });

      const [balanceRes, historyRes] = await Promise.all([
        makeRequest2(`${getWalletBalanceURL}/${facilityId}/${landlordId}`, "GET", {}),
        makeRequest2(`${getWalletHistoryURL}/${facilityId}/${landlordId}`, "GET", {})
      ]);

      console.log("📊 Balance Response:", balanceRes);
      console.log("📊 History Response:", historyRes);

      // Extract balance from nested response
      if (balanceRes.success) {
        const balance = balanceRes.data?.data?.balance ||
          balanceRes.data?.balance ||
          0;
        setBalance(balance);
        console.log("💰 Balance set to:", balance);
      }

      // Extract history from nested response
      if (historyRes.success) {
        const rows = historyRes.data?.data || historyRes.data || [];
        const historyArray = Array.isArray(rows) ? rows : [];
        setHistory(historyArray);
        console.log("📋 History entries:", historyArray.length);
      }
    } catch (err) {
      console.error("❌ Error fetching wallet:", err);
      toastify(err.message || "Failed to fetch wallet data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Mirrors formatCurrencyForPDF used in the SOA export, so PDF number
  // formatting stays consistent across reports.
  const formatCurrencyForPDF = (value) => {
    if (value === null || value === undefined) return "0.00";
    const num = typeof value === "number" ? value : parseFloat(String(value).replace(/[^0-9.-]+/g, ""));
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const exportWalletPDF = async () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString();

    if (LOGO_BASE64) {
      doc.addImage(LOGO_BASE64, "PNG", 14, 10, 40, 20);
    }

    // Company information on the right — same block as SOA export
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 78, 152);
    doc.text("Payserve Ltd", 195, 15, { align: "right" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text("Nairobi, Kenya.", 195, 27, { align: "right" });
    doc.text("www.payserve.co.ke", 195, 32, { align: "right" });
    doc.text("Email: support@payserve.co.ke", 195, 37, { align: "right" });

    // Document title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 78, 152);
    doc.text("WALLET STATEMENT", 14, 45);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(`Date: ${currentDate}`, 14, 55);

    // Current balance — right aligned, same position as SOA's "Current Balance"
    doc.setFont("helvetica", "bold");
    doc.text(
      `Available Balance: KES ${formatCurrencyForPDF(balance)}`,
      195,
      55,
      { align: "right" }
    );
    doc.setFont("helvetica", "normal");

    // Transaction table — Debit/Credit split like the SOA table, plus
    // Status since wallet entries carry an approval-ish status field.
    const headers = [["Date", "Type", "Description", "Reference", "Debit (KES)", "Credit (KES)", "Balance (KES)", "Status"]];
    const sourceLabels = {
      "rental-income": "Rental Income",
      "expense-deduction": "Expense Deduction",
      "withdrawal-request": "Withdrawal",
      "withdrawal-reversal": "Withdrawal Reversal",
      "manual-adjustment": "Manual Adjustment",
    };

    const data = history.map((t) => [
      t.createdAt ? new Date(t.createdAt).toLocaleString() : "N/A",
      t.type === "credit" ? "Credit" : "Debit",
      sourceLabels[t.source] || t.source || "N/A",
      t.reference || "N/A",
      t.type === "debit" ? formatCurrencyForPDF(t.amount) : "",
      t.type === "credit" ? formatCurrencyForPDF(t.amount) : "",
      formatCurrencyForPDF(t.balanceAfter),
      t.status || "N/A",
    ]);

    autoTable(doc, {
      startY: 65,
      head: headers,
      body: data,
      styles: { fontSize: 8 },
      headStyles: {
        fillColor: [0, 78, 152],
        fontSize: 8,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 16 },
        2: { cellWidth: 32 },
        3: { cellWidth: 26 },
        4: { cellWidth: 22, halign: "right" },
        5: { cellWidth: 22, halign: "right" },
        6: { cellWidth: 22, halign: "right" },
        7: { cellWidth: 18 },
      },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 65 },
    });

    // Totals summary — same "plain" theme block as the SOA export
    const totalDebit = history.filter((t) => t.type === "debit").reduce((sum, t) => sum + (t.amount || 0), 0);
    const totalCredit = history.filter((t) => t.type === "credit").reduce((sum, t) => sum + (t.amount || 0), 0);

    const summaryData = [
      ["Total Credits:", `KES ${formatCurrencyForPDF(totalCredit)}`],
      ["Total Debits:", `KES ${formatCurrencyForPDF(totalDebit)}`],
      ["Available Balance:", `KES ${formatCurrencyForPDF(balance)}`],
    ];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      body: summaryData,
      theme: "plain",
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 100, fontStyle: "bold", textColor: [0, 78, 152] },
        1: { cellWidth: 50, halign: "right", fontStyle: "bold", textColor: [0, 78, 152] },
      },
    });

    doc.save(`wallet_statement_${currentDate.replace(/\//g, "-")}.pdf`);
  };


  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  const openWithdrawModal = () => {
    setWithdrawAmount("");
    setWithdrawNote("");
    setShowWithdrawModal(true);
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);

    if (!amount || amount <= 0) {
      toastify("Enter a valid amount", "error");
      return;
    }
    if (amount > balance) {
      toastify("Withdrawal exceeds available balance", "error");
      return;
    }

    try {
      setSubmitting(true);
      const facilityId = await getItem("selectedFacilityId");
      const landlordId = await getItem("selectedCustomerId");

      const response = await makeRequest2(
        `${requestWithdrawalURL}/${facilityId}/${landlordId}`,
        "POST",
        { amount, notes: withdrawNote }
      );

      console.log("💳 Withdrawal Response:", response);

      // Handle both response formats
      const success = response?.success ?? response?.data?.success ?? false;
      const errorMsg = response?.error ?? response?.data?.error ?? "Withdrawal failed";

      if (success) {
        toastify("Withdrawal recorded successfully", "success");
        setShowWithdrawModal(false);
        // Refresh wallet data
        await fetchWallet();
      } else {
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error("❌ Withdrawal error:", err);
      toastify(err.message || "Failed to process withdrawal", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // DataTable Body Templates
  const typeBodyTemplate = (row) => (
    <span className={`badge bg-${row.type === "credit" ? "success" : "secondary"} bg-opacity-10 text-${row.type === "credit" ? "success" : "secondary"}`}>
      <i className={`ti ti-${row.type === "credit" ? "arrow-down-left" : "arrow-up-right"} me-1`}></i>
      {row.type === "credit" ? "Credit" : "Debit"}
    </span>
  );

  const sourceBodyTemplate = (row) => {
    const labels = {
      "rental-income": "Rental Income",
      "expense-deduction": "Expense Deduction",
      "withdrawal-request": "Withdrawal",
      "withdrawal-reversal": "Withdrawal Reversal",
      "manual-adjustment": "Manual Adjustment"
    };
    return labels[row.source] || row.source || "N/A";
  };

  const amountBodyTemplate = (row) => (
    <span className={`fw-semibold ${row.type === "credit" ? "text-success" : "text-danger"}`}>
      {row.type === "credit" ? "+" : "-"}{formatCurrency(row.amount)}
    </span>
  );

  const balanceAfterBodyTemplate = (row) => (
    <span className="fw-semibold">{formatCurrency(row.balanceAfter)}</span>
  );

  const dateBodyTemplate = (row) => {
    if (!row.createdAt) return "N/A";
    try {
      return new Date(row.createdAt).toLocaleString();
    } catch {
      return "Invalid Date";
    }
  };

  const statusBodyTemplate = (row) => {
    const statusColors = {
      completed: "success",
      pending: "warning",
      rejected: "danger"
    };
    const color = statusColors[row.status] || "secondary";
    return (
      <span className={`badge bg-${color} bg-opacity-10 text-${color}`}>
        {row.status || "N/A"}
      </span>
    );
  };

  return (
    <Layout>
      <div className="mb-4">
        <h4 className="fw-bold mb-1">Wallet</h4>
        <p className="text-muted small">Track your balance and withdrawal history</p>
      </div>

      {/* Balance Card */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div
            className="card border-0 shadow-sm"
            style={{
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
            }}
          >
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div>
                  <p className="text-white-50 small mb-1">Available Balance</p>
                  {loading ? (
                    <div className="spinner-border spinner-border-sm text-white" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <h2 className="fw-bold text-white mb-0">{formatCurrency(balance)}</h2>
                  )}
                </div>
                <button
                  className="btn btn-light fw-semibold px-4 py-2"
                  onClick={openWithdrawModal}
                  disabled={loading || balance <= 0}
                >
                  <i className="ti ti-arrow-up-right me-2"></i>
                  Request Withdrawal
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-4 col-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-3">
              <p className="text-muted small mb-1">Total Credits</p>
              <h6 className="fw-bold text-success mb-0">
                {formatCurrency(
                  history
                    .filter(t => t.type === 'credit')
                    .reduce((sum, t) => sum + t.amount, 0)
                )}
              </h6>
            </div>
          </div>
        </div>
        <div className="col-md-4 col-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-3">
              <p className="text-muted small mb-1">Total Debits</p>
              <h6 className="fw-bold text-danger mb-0">
                {formatCurrency(
                  history
                    .filter(t => t.type === 'debit')
                    .reduce((sum, t) => sum + t.amount, 0)
                )}
              </h6>
            </div>
          </div>
        </div>
        <div className="col-md-4 col-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-3">
              <p className="text-muted small mb-1">Transactions</p>
              <h6 className="fw-bold text-primary mb-0">{history.length}</h6>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Ledger */}
      <div className="row">
        <div className="col-md-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold mb-3 d-flex align-items-center justify-content-between">
                <span>
                  <i className="ti ti-list-details text-primary me-2"></i>
                  Transaction History
                </span>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={exportWalletPDF}
                  disabled={loading || history.length === 0}
                >
                  <i className="ti ti-file-download me-1"></i>
                  Export PDF
                </button>
              </h6>
              <DataTable
                value={history}
                loading={loading}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                emptyMessage="No transactions yet"
                sortField="createdAt"
                sortOrder={-1}
                className="p-datatable-sm"
                responsiveLayout="scroll"
              >
                <Column field="createdAt" header="Date" body={dateBodyTemplate} sortable style={{ minWidth: '150px' }} />
                <Column field="type" header="Type" body={typeBodyTemplate} style={{ minWidth: '120px' }} />
                <Column field="source" header="Description" body={sourceBodyTemplate} style={{ minWidth: '150px' }} />
                <Column field="reference" header="Reference" style={{ minWidth: '120px' }} />
                <Column field="amount" header="Amount" body={amountBodyTemplate} sortable style={{ minWidth: '120px' }} />
                <Column field="balanceAfter" header="Balance" body={balanceAfterBodyTemplate} sortable style={{ minWidth: '120px' }} />
                <Column field="status" header="Status" body={statusBodyTemplate} style={{ minWidth: '100px' }} />
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      {/* Withdrawal Modal */}
      <Dialog
        header="Request Withdrawal"
        visible={showWithdrawModal}
        style={{ width: "420px" }}
        onHide={() => setShowWithdrawModal(false)}
      >
        <div className="mb-3">
          <p className="text-muted small mb-1">Available Balance</p>
          <h5 className="fw-bold text-success">{formatCurrency(balance)}</h5>
        </div>

        <div className="mb-3">
          <label className="form-label small fw-semibold">Amount (KES)</label>
          <input
            type="number"
            className="form-control"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            placeholder="0.00"
            min="0"
            max={balance}
          />
          {balance > 0 && (
            <small className="text-muted">
              Max: {formatCurrency(balance)}
            </small>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label small fw-semibold">Note (optional)</label>
          <textarea
            className="form-control"
            rows={2}
            value={withdrawNote}
            onChange={(e) => setWithdrawNote(e.target.value)}
            placeholder="e.g. Payout to bank account ending 1234"
          />
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowWithdrawModal(false)}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={handleWithdraw}
            disabled={submitting || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Processing...
              </>
            ) : (
              "Confirm Withdrawal"
            )}
          </button>
        </div>
      </Dialog>
    </Layout>
  );
}

export default Wallet;